import { type AnyRoute, createRoute } from "@tanstack/react-router";
import { lazy } from "react";
import { UgcLayout } from "../components/UgcLayout";
import { parseCampaignStatusFilter, parseWizardStep } from "../lib/routing";

const UgcDashboardPage = lazy(() =>
  import("../components/UgcDashboardPage").then((module) => ({ default: module.UgcDashboardPage })),
);
const IdentityLibraryPage = lazy(() =>
  import("../components/IdentityLibraryPage").then((module) => ({ default: module.IdentityLibraryPage })),
);
const IdentityEditorPage = lazy(() =>
  import("../components/IdentityEditorPage").then((module) => ({ default: module.IdentityEditorPage })),
);
const KitLibraryPage = lazy(() =>
  import("../components/KitLibraryPage").then((module) => ({ default: module.KitLibraryPage })),
);
const KitEditorPage = lazy(() =>
  import("../components/KitEditorPage").then((module) => ({ default: module.KitEditorPage })),
);
const CampaignWizardPage = lazy(() =>
  import("../components/CampaignWizardPage").then((module) => ({ default: module.CampaignWizardPage })),
);
const CampaignDetailPage = lazy(() =>
  import("../components/CampaignDetailPage").then((module) => ({ default: module.CampaignDetailPage })),
);

/** UGC Studio routes, mounted beneath the existing auth-gated app shell. */
export function createUgcRoutes<TParent extends AnyRoute>(parent: TParent) {
  const layout = createRoute({
    getParentRoute: () => parent,
    id: "ugc-layout",
    component: UgcLayout,
  });

  const dashboard = createRoute({
    getParentRoute: () => layout,
    path: "/ugc",
    validateSearch: (search: Record<string, unknown>) => ({
      status: parseCampaignStatusFilter(search.status),
      q: typeof search.q === "string" ? search.q : "",
    }),
    component: UgcDashboardPage,
  });
  const identities = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/identities",
    component: IdentityLibraryPage,
  });
  const identityCreate = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/identities/new",
    component: IdentityEditorPage,
  });
  const identityEdit = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/identities/$identityId/edit",
    component: IdentityEditorPage,
  });
  const brandKits = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/brand-kits",
    component: () => <KitLibraryPage kind="brand" />,
  });
  const brandKitCreate = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/brand-kits/new",
    component: () => <KitEditorPage kind="brand" />,
  });
  const brandKitEdit = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/brand-kits/$kitId/edit",
    component: () => <KitEditorPage kind="brand" />,
  });
  const productKits = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/product-kits",
    component: () => <KitLibraryPage kind="product" />,
  });
  const productKitCreate = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/product-kits/new",
    component: () => <KitEditorPage kind="product" />,
  });
  const productKitEdit = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/product-kits/$kitId/edit",
    component: () => <KitEditorPage kind="product" />,
  });
  const campaignWizard = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/campaigns/new",
    validateSearch: (search: Record<string, unknown>) => ({ step: parseWizardStep(search.step) }),
    component: CampaignWizardPage,
  });
  const campaignDetail = createRoute({
    getParentRoute: () => layout,
    path: "/ugc/campaigns/$campaignId",
    component: CampaignDetailPage,
  });

  return [
    layout.addChildren([
      dashboard,
      identities,
      identityCreate,
      identityEdit,
      brandKits,
      brandKitCreate,
      brandKitEdit,
      productKits,
      productKitCreate,
      productKitEdit,
      campaignWizard,
      campaignDetail,
    ]),
  ];
}
